import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useLang } from '../contexts/LangContext';
import { useAuth } from '../contexts/AuthContext';
import { permissionGroupsApi, PermissionGroup } from '../api/permissionGroupsApi';
import { accountsApi, Operator } from '../api/accountsApi';
import { PermissionGroupImportModal } from '../components/PermissionGroups/PermissionGroupImportModal';

export const PermissionGroupList: React.FC = () => {
  const { t } = useLang();
  const { isSuperAdmin } = useAuth();
  const navigate = useNavigate();
  const [permissionGroups, setPermissionGroups] = useState<PermissionGroup[]>([]);
  const [operators, setOperators] = useState<Operator[]>([]);
  const [selectedOperator, setSelectedOperator] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [seeding, setSeeding] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);

  const loadOperators = async () => {
    if (isSuperAdmin) {
      try {
        const operatorsData = await accountsApi.getOperators();
        setOperators(operatorsData);
      } catch (error) {
        console.error('Error loading operators:', error);
      }
    }
  };

  const loadPermissionGroups = async (operatorId?: string) => {
    try {
      setLoading(true);
      setError(null);
      
      // SUPERADMIN can view either specific operator's groups or their own groups
      // OPERATOR always loads their own groups
      const groups = await permissionGroupsApi.getAll(operatorId);
      setPermissionGroups(groups);
    } catch (err: any) {
      setError(err.response?.data?.message || t('errorLoadingPermissionGroups'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOperators();
    if (!isSuperAdmin) {
      // OPERATOR loads their own groups immediately
      loadPermissionGroups();
    } else {
      // SUPERADMIN loads their own groups by default (no operator selected)
      loadPermissionGroups();
    }
  }, [isSuperAdmin]);

  useEffect(() => {
    if (isSuperAdmin && selectedOperator) {
      loadPermissionGroups(selectedOperator);
    } else if (isSuperAdmin && !selectedOperator) {
      // When SUPERADMIN deselects operator, load their own groups
      loadPermissionGroups();
    }
  }, [selectedOperator, isSuperAdmin]);

  const handleCreateComprehensiveGroups = async () => {
    try {
      setSeeding(true);
      // For SUPERADMIN: pass selectedOperator (could be undefined for own groups)
      // For OPERATOR: don't pass any operatorId (handled by backend)
      const operatorId = isSuperAdmin ? selectedOperator : undefined;
      await permissionGroupsApi.createComprehensiveDefaultGroups(operatorId);
      await loadPermissionGroups(operatorId);
      alert(t('comprehensiveGroupsCreatedSuccess') || 'Đã tạo nhóm phân quyền toàn diện thành công!');
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || t('errorCreatingComprehensiveGroups') || 'Lỗi tạo nhóm phân quyền toàn diện';
      
      // Check if it's the "no functions" error and provide better guidance
      if (errorMessage.includes('chức năng hệ thống') || errorMessage.includes('functions')) {
        const shouldGoToFunctions = window.confirm(
          'Không có chức năng hệ thống nào được tìm thấy.\n\n' +
          'Để tạo nhóm phân quyền, bạn cần tạo các chức năng hệ thống trước.\n\n' +
          'Bạn có muốn chuyển đến trang Chức năng để tạo các chức năng mặc định không?'
        );
        
        if (shouldGoToFunctions) {
          navigate('/functions');
        }
      } else {
        alert(errorMessage);
      }
    } finally {
      setSeeding(false);
    }
  };

  const handleDelete = async (id: string, name: string) => {
    if (!window.confirm(`${t('confirmDeleteGroup')} "${name}"?`)) {
      return;
    }

    try {
      await permissionGroupsApi.delete(id);
      await loadPermissionGroups(isSuperAdmin ? selectedOperator : undefined);
    } catch (err: any) {
      alert(err.response?.data?.message || t('errorDeletingPermissionGroup'));
    }
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      setError(null);
      await permissionGroupsApi.exportPermissionGroups(
        isSuperAdmin ? selectedOperator || undefined : undefined,
      );
      alert(t('exportPermissionGroupsSuccess'));
    } catch (err: any) {
      setError(err.response?.data?.message || t('failedToLoad'));
    } finally {
      setExporting(false);
    }
  };

  const openImportModal = () => {
    setShowImportModal(true);
  };

  const importScopeLabel = isSuperAdmin
    ? selectedOperator
      ? operators.find((operator) => operator.id === selectedOperator)?.name ||
        t('viewingOperatorGroups')
      : t('myPermissionGroups')
    : t('myPermissionGroups');

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto py-8">
        <div className="flex items-center justify-center h-64">
          <div className="text-lg text-gray-600">{t('loadingPermissionGroups')}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto py-8">
      {/* Header */}
      <div className="mb-8">
        <nav className="text-sm text-gray-500 mb-4">
          <span>{t('accountManagement')}</span> / <span className="text-gray-900">{t('permissionGroups')}</span>
        </nav>
        <h1 className="text-3xl font-bold text-gray-900 mb-2">{t('permissionGroups')}</h1>
        <p className="text-gray-600">{t('managePermissionGroups')}</p>
      </div>

      {/* Operator Selection for SUPERADMIN */}
      {isSuperAdmin && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex items-center gap-4">
            <label className="text-sm font-medium text-gray-700 whitespace-nowrap">
              {t('selectOperator')}:
            </label>
            <select
              value={selectedOperator}
              onChange={(e) => setSelectedOperator(e.target.value)}
              className="flex-1 max-w-md px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
            >
              <option value="">{t('myPermissionGroups')} (SUPERADMIN)</option>
              {operators.map((operator) => (
                <option key={operator.id} value={operator.id}>
                  {operator.name} ({operator.email})
                </option>
              ))}
            </select>
            <div className="text-sm text-gray-500">
              {selectedOperator ? (
                <span>{t('viewingOperatorGroups')}</span>
              ) : (
                <span>{t('viewingOwnGroups')}</span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Actions Bar */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <div className="flex flex-wrap gap-4 items-center justify-between">
          <div className="flex gap-4">
            <Link
              to="/permission-groups/add"
              className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-lg hover:from-cyan-600 hover:to-blue-600 transition-all duration-200 shadow-md"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              {t('addPermissionGroup')}
            </Link>
            
            <button
              onClick={handleCreateComprehensiveGroups}
              disabled={seeding}
              className="inline-flex items-center px-4 py-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors duration-200 disabled:opacity-50"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
              {seeding ? t('creating') : 'Tạo nhóm phân quyền toàn diện'}
            </button>

            <button
              onClick={handleExport}
              disabled={exporting || permissionGroups.length === 0}
              className="inline-flex items-center px-4 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors duration-200 disabled:opacity-50"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              {exporting ? t('loading') : t('exportPermissionGroups')}
            </button>

            <button
              onClick={openImportModal}
              className="inline-flex items-center px-4 py-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors duration-200"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
              {t('importPermissionGroups')}
            </button>
          </div>

          <div className="text-sm text-gray-500">
            {t('total')}: <span className="font-semibold text-gray-900">{permissionGroups.length}</span> {t('groups')}
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <div className="flex items-center">
            <svg className="w-5 h-5 text-red-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="text-red-800">{error}</span>
            <button
              onClick={() => loadPermissionGroups(isSuperAdmin ? selectedOperator : undefined)}
              className="ml-auto px-3 py-1 bg-red-100 text-red-700 rounded hover:bg-red-200 transition-colors"
            >
              {t('tryAgain')}
            </button>
          </div>
        </div>
      )}

      {/* Permission Groups Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {permissionGroups.length === 0 ? (
          <div className="p-8 text-center">
            <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
            <h3 className="text-lg font-medium text-gray-900 mb-2">{t('noPermissionGroups')}</h3>
            <p className="text-gray-500 mb-4">{t('getStartedPermissionGroups')}</p>
            <div className="space-x-3">
              <Link
                to="/permission-groups/add"
                className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-lg hover:from-cyan-600 hover:to-blue-600 transition-all duration-200"
              >
                {t('createFirstGroup')}
              </Link>
              <button
                onClick={handleCreateComprehensiveGroups}
                disabled={seeding}
                className="inline-flex items-center px-4 py-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors duration-200 disabled:opacity-50"
              >
                {seeding ? t('creating') : 'Tạo nhóm phân quyền toàn diện'}
              </button>
            </div>
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('stt')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('groupName')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('description')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('functions')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('userCount')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('actions')}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {permissionGroups.map((group, index) => (
                <tr key={group.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {index + 1}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{group.name}</div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900">{group.description || '-'}</div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-500">
                      {group.permissions.length} {group.permissions.length !== 1 ? t('functions').toLowerCase() : t('function').toLowerCase()}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      {group._count.users} {group._count.users !== 1 ? t('users') : t('user')}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-3">
                    <Link
                      to={`/permission-groups/${group.id}/edit`}
                      className="text-cyan-600 hover:text-cyan-900 transition-colors"
                    >
                      {t('edit')}
                    </Link>
                    <button
                      onClick={() => handleDelete(group.id, group.name)}
                      className="text-red-600 hover:text-red-900 transition-colors"
                    >
                      {t('delete')}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <PermissionGroupImportModal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        onImported={() => loadPermissionGroups(isSuperAdmin ? selectedOperator : undefined)}
        operatorId={isSuperAdmin ? selectedOperator || undefined : undefined}
        scopeLabel={importScopeLabel}
        t={t}
      />
    </div>
  );
}; 